import { Container } from "./styles";
import { WidgetSlot } from "./components/WidgetSlot";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Error from "../../components/Error";
import { useLoading } from "../../hooks/loading";
import { useSelector } from "react-redux";
import {
  requestLPlusCP,
  requestLPlusExternal,
} from "./controller/leadsterplus.controller";
import { addCookieLPlus, removeCookieLPlus } from "./utils/leadsterplus.utils";

export function LeadsterPlus() {
  const { token } = useSelector((state) => state.auth);
  const { contract } = useSelector((state) => state.initialSettings);
  const [accessAuth, setAccessAuth] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [refreshToken, setRefreshToken] = useState("");
  const { handleLoading } = useLoading();
  const intl = useIntl();
  const isLoadingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const handleLeadsterReady = useCallback(() => {
    handleLoading(false);
    // Não reseta hasError aqui - pode sobrescrever erros legítimos
  }, [handleLoading]);

  const handleLeadsterLoadStart = useCallback(() => {
    // Não chama handleLoading aqui - já gerenciado pelo auth process
    // handleLoading(true);
  }, []);

  const handleLeadsterError = useCallback(() => {
    handleLoading(false);
    setHasError(true);
    setAccessAuth(false);
    setRefreshToken("");
  }, [handleLoading]);

  /**
   * @name negateAccessAuth
   * @description limpa cookie, nega autorizacao de acesso ao widget e sinaliza erro na pagina
   */
  const negateAccessAuth = useCallback(() => {
    setAccessAuth(false);
    removeCookieLPlus();
    setHasError(true);
    setRefreshToken("");
  }, []);

  /**
   * @name manageAccessAuthWidget
   * @description gerencia autorizacao de acesso ao slot widget do L+
   */
  const manageAccessAuthWidget = useCallback(async () => {
    // Previne múltiplas execuções simultâneas
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      handleLoading(true);
      setAccessAuth(false);
      setHasError(false);
      setRefreshToken("");

      const opportunityId = contract?.value;

      if (!opportunityId) {
        throw new Error("Contract opportunity ID is missing");
      }

      if (!token) {
        throw new Error("Token is missing");
      }

      const responseInternal = await requestLPlusCP(opportunityId, token);

      if (!responseInternal) {
        negateAccessAuth();
        return;
      }

      const { bootstrapJwt, lplusBaseUrl } = responseInternal;

      const responseExternal = await requestLPlusExternal(
        bootstrapJwt,
        lplusBaseUrl,
      );

      if (responseExternal) {
        const refreshToken = responseExternal.data?.refresh_token;

        addCookieLPlus(refreshToken);
        setRefreshToken(refreshToken);
        setAccessAuth(true);
        setHasError(false);
      } else {
        negateAccessAuth();
      }
    } catch (error) {
      negateAccessAuth();
    } finally {
      isLoadingRef.current = false;
      handleLoading(false);
    }
  }, [contract?.value, token, handleLoading, negateAccessAuth]);

  useEffect(() => {
    // Só executa uma vez quando contract?.value e token estiverem disponíveis
    if (contract?.value && token && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      manageAccessAuthWidget();
    }

    // Cleanup ao desmontar: reseta refs e estados
    return () => {
      hasInitializedRef.current = false;
      isLoadingRef.current = false;
      handleLoading(false); // garante que o loading seja removido
    };
  }, [contract?.value, token]);

  return (
    <Container>
      {hasError && (
        <Error
          description={intl.formatMessage({
            id: "leadster_plus.widget_error",
          })}
        />
      )}

      {accessAuth && (
        <WidgetSlot
          refreshToken={refreshToken}
          onWidgetReady={handleLeadsterReady}
          onWidgetLoadStart={handleLeadsterLoadStart}
          onWidgetError={handleLeadsterError}
        />
      )}
    </Container>
  );
}
